import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Keyable from '../../../../types/keyable';
import httpErrorHandling from '../../../../utils/httpErrorHandling';
import { confirmServicesStep } from './servicesRequest';
import { OnboardingStepState } from '../../onboardingSlice';
import SectorExternal from '../../../../common/interfaces/SectorExternal';
import IPicture from '../../../../common/interfaces/IPicture';
import { ITranslated } from '../../../../common/interfaces/ServicesInterfaces';

export interface ServicesSectorTestimonialsListCommonState {
  id: string;
  name: string;
  title: string;
  translated?: ITranslated;
  isCustom: boolean;
}
export interface ServicesSectorTestimonialsRate extends Keyable {
  description?: string;
  price?: string;
  currency: string;
  isActive: boolean;
}

export interface ServicesSectorTestimonialsState extends Keyable {
  year?: string;
  yearChecked?: boolean;
  explanatory?: string;
  specialities?: ServicesSectorTestimonialsListCommonState[];
  benefits?: ServicesSectorTestimonialsListCommonState[];
  audiences?: ServicesSectorTestimonialsListCommonState[];
  appointments?: ServicesSectorTestimonialsListCommonState[];
  rates?: ServicesSectorTestimonialsRate[];
  ratesAdditional?: string;
  illustration?: IPicture;
}

export interface ServicesSectorState extends Keyable {
  sector: SectorExternal;
  name: string;
  primary?: boolean;
  selected?: boolean;
  testimonials?: ServicesSectorTestimonialsState;
}

interface ServicesState extends OnboardingStepState {
  sectors: ServicesSectorState[];
}

const initialState: ServicesState = {
  inProgress: false,
  isDone: false,
  doRedirectFurther: false,
  sectors: [],
  error: '',
};

export const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    setServicesStateDefault: () => {
      return { ...initialState };
    },
    startProcessingServices: state => {
      state.inProgress = true;
    },
    addSectors: (state, action: PayloadAction<ServicesSectorState[]>) => {
      state.sectors = action.payload;
    },
    updateSectorTestimonials: (state, action: PayloadAction<ServicesSectorTestimonialsState>) => {
      state.sectors = state.sectors.map(it => {
        if (it.key === action.payload.key) {
          return { ...it, testimonials: { ...it.testimonials, ...action.payload } };
        }
        return it;
      });
    },
    submitServices: state => {
      state.inProgress = false;
      state.isDone = true;
      state.doRedirectFurther = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(confirmServicesStep.fulfilled, state => {
      state.error = '';
      state.doRedirectFurther = true;
    });
    builder.addCase(confirmServicesStep.pending, state => {
      state.error = '';
    });
    builder.addCase(confirmServicesStep.rejected, (state, action) => {
      state.error = httpErrorHandling(action);
    });
  },
});

export const { setServicesStateDefault, startProcessingServices, submitServices, addSectors, updateSectorTestimonials } =
  servicesSlice.actions;

export default servicesSlice.reducer;
